import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { makeStyles, useTheme, ThemeProvider, createTheme } from "@material-ui/core/styles";
import FileUpload from "./FileUpload";
import { ItemContext } from "../../Context/ItemContext";
import { useHistory, Link } from "react-router-dom"; // Add Link

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createItem, updateItem } from "../../actions/itemActions";
import { useSnackbar } from "../../utils/useSnackbar";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import PhotoCamera from "@material-ui/icons/PhotoCamera";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import { StockContext } from "../../Context/StockContext";
import { createUnit, getAllUnits } from "../../actions/unitActions";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";
import { useCompany } from "../../Context/CompanyContext";
import ExpiryDatePicker from "../common/ExpiryDatePicker";
import { getTaxLabel } from "../../utils/currency";
import {
  createCategory,
  getAllCategories,
  deleteCategory,
} from "../../actions/categoryActions";
import {
  FormControl,
  InputLabel,
  MenuItem,
  ListSubheader,
  Select,
  Container,
  FormControlLabel,
  Switch,
  FormGroup,
  Radio,
  FormHelperText,
  CircularProgress,
  useMediaQuery,
  InputAdornment,
} from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    backgroundColor: "#1976D2",
    marginLeft: 0,
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: "white",
  },
  inputStyle: {
    width: "100%",
    fontFamily: "kruti-101",
  },
  formControl: {
    width: "100%",
  },
  container: {
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    borderRadius: "8px",
    padding: "10px",
  },
  radioLabel: {
    // margin: theme.spacing(1, 0),
    display: "block",
  },
  input: {
    display: "none",
  },
  disabledMenuItem: {
    color: theme.palette.text.disabled, // Greyed out style for inactive categories
  },
  quickCreateItem: {
    position: "sticky",
    bottom: 0,
    zIndex: 1,
    color: "#d96f17",
    fontWeight: 700,
    backgroundColor: "#fffaf5",
    borderTop: "1px solid #fed7aa",
    "&:hover": { backgroundColor: "#fff3e6" },
  },
  quickDialogContent: {
    display: "grid",
    gap: theme.spacing(2),
    minWidth: 280,
    paddingTop: theme.spacing(2),
  },
  menuPaper: {
    "&::-webkit-scrollbar": { width: 5 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      background: "#94a3b8",
      borderRadius: 999,
    },
  },
  searchHeader: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#fff",
    padding: theme.spacing(1, 1.25),
    borderBottom: "1px solid #eef1f5",
    boxShadow: "0 2px 4px rgba(15, 23, 42, 0.04)",
  },
  searchInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: "#f8fafc",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#ef7f1a",
    },
  },
  noResultsItem: {
    color: theme.palette.text.disabled,
    fontStyle: "italic",
    justifyContent: "center",
    opacity: 1,
  },
}));

// Rendered as a direct child of <Select>, so two MUI internals apply to it:
// MenuList attaches a ref to its children (hence forwardRef - a plain
// function component triggered "Function components cannot be given refs"),
// and SelectInput clones every child with `value: undefined` plus its own
// onClick/role/selected props. The search text therefore travels as
// `searchValue` (a `value` prop would be wiped), and the injected props are
// deliberately NOT spread - forwarding onClick would "select" the header.
const SearchListHeader = React.forwardRef(function SearchListHeader(
  { classes, searchValue, onChange, placeholder },
  ref
) {
  return (
  <ListSubheader ref={ref} className={classes.searchHeader}>
    <TextField
      autoFocus
      size="small"
      placeholder={placeholder}
      value={searchValue}
      onChange={onChange}
      onKeyDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      variant="outlined"
      fullWidth
      className={classes.searchInput}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" style={{ color: "#94a3b8" }} />
          </InputAdornment>
        ),
      }}
    />
  </ListSubheader>
  );
});

// The one shape the form works with. clear()/handleClose used to reset to a
// different, partial object (no `units`, `stockQty: null`, `category: ""`) -
// and clear() runs every time the dialog opens for a new item - so saving an
// item with Track Stock on hit `stockQty.initialQty = ...` on null (or
// `units.stock.quantity` on undefined), threw a TypeError that was only
// console-logged, and the item was never created: the "sometimes a new item
// doesn't show up in the Items list / POS" glitch.
const createEmptyItemData = (vat = "") => ({
  itemName: "",
  category: {
    categoryName: "",
    categoryId: null,
  },
  units: {
    sale: { unit: null, price: "", minQty: "" },
    purchase: { unit: null, cost: "" },
    stock: { unit: null, quantity: 0 },
  },
  barcode: "",
  img: null,
  toggleStock: false,
  trackExpiry: false,
  isStaffReq: false,
  stockQty: {
    initialQty: 0,
    currentQty: 0,
    minQty: 0,
    maxQty: 0,
  },
  vat,
});

const DialogTitle = (props) => {
  const { children, onClose, ...other } = props;
  const classes = useStyles();
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
};

const DialogContent = (props) => {
  const classes = useStyles();
  return <MuiDialogContent {...props} />;
};

// Light caption that starts a group of fields (coloured dot + small label).
const GroupLabel = ({ order, color, children }) => (
  <Grid item xs={12} style={{ order, paddingBottom: 0, paddingTop: 10 }}>
    <Typography style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", color: "#64748b", textTransform: "uppercase" }}>
      <span
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          marginRight: 6,
          verticalAlign: "middle",
        }}
      />
      {children}
    </Typography>
  </Grid>
);

// Section heading inside the form's grid. `order` places it among the fields
// (the fields keep their own code order; only their on-screen position is set).
const SectionTitle = ({ children, order }) => (
  <Grid item xs={12} style={{ order, paddingBottom: 0 }}>
    <Typography
      style={{
        fontSize: 11.5,
        fontWeight: 800,
        letterSpacing: "0.06em",
        color: "#9a3412",
        textTransform: "uppercase",
        borderBottom: "1px solid #fed7aa",
        paddingBottom: 4,
      }}
    >
      {children}
    </Typography>
  </Grid>
);

const DialogActions = (props) => {
  const classes = useStyles();
  return <MuiDialogActions {...props} />;
};
// const units = [
//   {
//     _id: "661a1f3b4b3f5a001f3e1001",
//     name: "Kilogram",
//     abbreviation: "kg",
//     baseUnit: null,
//     conversionFactor: 1,
//     type: ["STOCK", "SALE", "PURCHASE", "ADJUSTMENT"],
//   },
//   {
//     _id: "661a1f3b4b3f5a001f3e1002",
//     name: "Gram",
//     abbreviation: "g",
//     baseUnit: "661a1f3b4b3f5a001f3e1001", // Reference to Kilogram
//     conversionFactor: 0.001,
//     type: ["STOCK", "SALE", "PURCHASE"],
//   },
//   {
//     _id: "661a1f3b4b3f5a001f3e1003",
//     name: "Liter",
//     abbreviation: "L",
//     baseUnit: null,
//     conversionFactor: 1,
//     type: ["STOCK", "SALE", "PURCHASE", "ADJUSTMENT"],
//   },
//   {
//     _id: "661a1f3b4b3f5a001f3e1004",
//     name: "Milliliter",
//     abbreviation: "mL",
//     baseUnit: "661a1f3b4b3f5a001f3e1003", // Reference to Liter
//     conversionFactor: 0.001,
//     type: ["STOCK", "SALE", "PURCHASE"],
//   },
// ];

const AddItem = ({ setOpen, open, initialItemName, onCreated }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const history = useHistory();

  const location = useLocation();
  const effectiveProfile = useEffectiveProfile();
  const taxLabel = getTaxLabel(effectiveProfile?.currency);
  // Special Feature toggle (see SuperAdmin > Global Settings) - when off,
  // Minimum Sale Quantity is never enforced anywhere (see CartContext.js's
  // getItemMinSaleQty), so there's nothing for this field to do - hide it
  // entirely instead of showing a control that silently has no effect.
  const { company } = useCompany();
  const minSaleQuantityFeatureOn = company?.minSaleQuantityEnabled === true;
  const units = useSelector((state) => state.units.units);
  // console.log(units);
  const [itemData, setItemData] = useState(() =>
    createEmptyItemData(effectiveProfile?.commonVatRate || "")
  );
  // const [itemData, setItemData] = useState({
  //   itemName: "",
  //   category: {
  //     categoryName: "",
  //     categoryId: "",
  //   },
  //   unitPrice: "",
  //   costPrice: "",
  //   barcode: "",
  //   unit: "",
  //   itemId: [],
  //   img: null,
  //   toggleStock: false,
  //   isStaffReq: false,
  //   stockQty: {
  //     initialQty: 0,
  //     currentQty: 0,
  //     minQty: 0,
  //     maxQty: 0,
  //   },

  // });
  const { itemId, setItemId } = useContext(ItemContext);
  const dispatch = useDispatch();
  const allItems = useSelector((state) => state.items.items);
  const item = itemId ? allItems.find((i) => i._id === itemId) || null : null;
  const showItemVatField =
    effectiveProfile?.vatOptions === "itemBased" &&
    (!effectiveProfile || effectiveProfile.vatType !== "common");
  // console.log("Edit item", item);
  const { categories } = useSelector((state) => state.categories);
  const selectedSaleUnit = units.find((unit) => unit._id === itemData?.units?.sale?.unit);
  const selectedStockUnit = units.find((unit) => unit._id === itemData?.units?.stock?.unit);

  const [isEditMode, setIsEditMode] = useState(false); // New state variable
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("profile")));
  const [openSnackbar, closeSnackbar] = useSnackbar();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickCreateType, setQuickCreateType] = useState(null);
  const [quickCategoryName, setQuickCategoryName] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [saleUnitSearch, setSaleUnitSearch] = useState("");
  const [purchaseUnitSearch, setPurchaseUnitSearch] = useState("");
  const [stockUnitSearch, setStockUnitSearch] = useState("");
  const [pendingCategoryName, setPendingCategoryName] = useState("");
  const [quickUnitName, setQuickUnitName] = useState("");
  const [quickUnitAbbreviation, setQuickUnitAbbreviation] = useState("");
  const [isQuickCreating, setIsQuickCreating] = useState(false);
  const keepItemDialogOpenRef = useRef(false);
  const { stock, trackStock, setTrackStock, getCurrentStockQty } = useContext(StockContext);
  // const [option, setOption] = useState("manualAdd"); // Default to fileUpload for new items
  const handleTrackStockChange = (event) => {
    setTrackStock(event.target.checked);
    setItemData({
      ...itemData,
      toggleStock: event.target.checked,
    });
  };

  const menuProps = {
    PaperProps: {
      classes: { root: classes.menuPaper },
      style: {
        maxHeight: 280, // Adjust this value as needed
        overflowY: "auto", // Enables vertical scrolling
        scrollbarWidth: "thin",
        scrollbarColor: "#64748b transparent",
      },
    },
  };

  const sortedCategories = [...categories].sort((first, second) =>
    (first.categoryName || "").localeCompare(second.categoryName || "", undefined, {
      sensitivity: "base",
    })
  );
  const startsWithQuery = (text, query) =>
    (text || "").toLowerCase().startsWith(query.trim().toLowerCase());

  const filteredCategories = sortedCategories.filter((category) =>
    startsWithQuery(category.categoryName, categorySearch)
  );

  const filterUnitsByQuery = (query) => {
    if (!query.trim()) return units;
    return units.filter(
      (unit) =>
        startsWithQuery(unit.name, query) ||
        startsWithQuery(unit.abbreviation, query)
    );
  };

  const filteredSaleUnits = useMemo(
    () => filterUnitsByQuery(saleUnitSearch),
    [units, saleUnitSearch]
  );
  const filteredPurchaseUnits = useMemo(
    () => filterUnitsByQuery(purchaseUnitSearch),
    [units, purchaseUnitSearch]
  );
  const filteredStockUnits = useMemo(
    () => filterUnitsByQuery(stockUnitSearch),
    [units, stockUnitSearch]
  );

  const openQuickCreate = (type) => {
    // This action originates in a Select menu. Keep the parent item dialog
    // mounted while that menu closes and the quick-create dialog opens.
    setOpen(true);
    if (type === "category") keepItemDialogOpenRef.current = true;
    setQuickCreateType(type);
    setQuickCategoryName("");
    setQuickUnitName("");
    setQuickUnitAbbreviation("");
  };

  const closeQuickCreate = () => {
    if (!isQuickCreating) {
      keepItemDialogOpenRef.current = false;
      setQuickCreateType(null);
    }
  };

  const handleQuickCreate = async () => {
    if (quickCreateType === "category") {
      const categoryName = quickCategoryName.trim();
      if (!categoryName) {
        openSnackbar("Enter a category name");
        return;
      }
      setIsQuickCreating(true);
      try {
        setPendingCategoryName(categoryName);
        const createdResult = await dispatch(
          createCategory({ categoryName, isActive: true, isDeleted: false })
        );
        const category = createdResult?.category || createdResult?.data || createdResult;
        if (category?._id) {
          setItemData((current) => ({
            ...current,
            category: { categoryName: category.categoryName, categoryId: category._id },
          }));
          setPendingCategoryName("");
          clearFieldError("category");
          openSnackbar(`Category "${category.categoryName}" created`);
        } else {
          // ADD_NEW_CATEGORY has already populated the store; the effect
          // below resolves it by name without triggering a page-wide reload.
          openSnackbar(`Category "${categoryName}" created`);
        }
        setOpen(true);
        setQuickCreateType(null);
        // Material-UI can emit the parent dialog's close event while a nested
        // dialog is being removed. Re-assert the parent state on the next tick.
        if (quickCreateType === "category") {
          setTimeout(() => {
            setOpen(true);
            keepItemDialogOpenRef.current = false;
          }, 0);
        }
      } catch (error) {
        openSnackbar(error.message || "Unable to create category");
      } finally {
        setIsQuickCreating(false);
      }
      return;
    }

    const name = quickUnitName.trim();
    const abbreviation = quickUnitAbbreviation.trim();
    if (!name || !abbreviation) {
      openSnackbar("Enter a unit name and abbreviation");
      return;
    }
    setIsQuickCreating(true);
    try {
      const unit = await dispatch(createUnit({ name, abbreviation }, openSnackbar));
      if (!unit?._id) throw new Error("Unable to create unit");
      setItemData((current) => {
        // The dialog can be reopened while a quick-create request finishes;
        // rebuild the missing unit shape instead of reading units.sale/etc.
        const currentUnits = current.units || {};
        const selectedUnit = currentUnits[quickCreateType] || {};
        return {
          ...current,
          units: {
            ...currentUnits,
            [quickCreateType]: {
              ...selectedUnit,
              unit: unit._id,
            },
          },
        };
      });
      clearFieldError(`units.${quickCreateType}.unit`);
      setQuickCreateType(null);
    } catch (error) {
      // createUnit displays the server error; retain values for correction.
    } finally {
      setIsQuickCreating(false);
    }
  };

  // Some API deployments return the category object wrapped differently. When
  // the store refreshes, resolve by name as a fallback and select it directly.
  useEffect(() => {
    if (!pendingCategoryName) return;
    const createdCategory = categories.find(
      (category) =>
        category.categoryName?.toLowerCase() === pendingCategoryName.toLowerCase()
    );
    if (!createdCategory?._id) return;
    setItemData((current) => ({
      ...current,
      category: {
        categoryName: createdCategory.categoryName,
        categoryId: createdCategory._id,
      },
    }));
    setPendingCategoryName("");
  }, [categories, pendingCategoryName]);

  const handleIsStaffRequiredChange = (event) => {
    // setTrackStock(event.target.checked);
    setItemData({
      ...itemData,
      isStaffReq: event.target.checked,
    });
  };
  useEffect(() => {
    if (!units.length) {
      dispatch(getAllUnits());
    }

    // dispatch(getItemsByUser({ search: user.result?._id || user.result.googleId }));
  }, [dispatch]);
  useEffect(() => {
    // See FloatingFooter.js - `categories` must not be a dependency, or an
    // empty catalog turns this into an infinite refetch loop (every fetch
    // returns a new-but-still-empty array reference).
    if (!categories.length) {
      dispatch(getAllCategories());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // useEffect(() => {
  //   if (itemId) {
  //     setOption("manualAdd");
  //   }
  //   // else {
  //   //   setOption("fileUpload");
  //   // }
  // }, [itemId]);

  // const handleSubmitItem = (e) => {
  //   e.preventDefault();
  //   if (validateForm()) {
  //     if (itemId) {
  //       dispatch(updateItem(itemId, itemData, openSnackbar));
  //       window.location.reload();
  //     } else {
  //       const updatedItemData = { ...itemData };
  //       // console.log(updatedItemData)

  //       updatedItemData.vat = profile?.userProfile?.commonVatRate;
  //       updatedItemData.stockQty.initialQty =
  //         updatedItemData.units?.stock?.quantity;
  //       updatedItemData.stockQty.currentQty =
  //         updatedItemData.units?.stock?.quantity;
  //       setItemData(updatedItemData);
  //       dispatch(createItem(updatedItemData, openSnackbar));
  //       console.log(updatedItemData)

  //       window.location.reload();
  //     }
  //     clear();
  //     handleClose();
  //   }
  // };

  

  const handleSubmitItem = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);
      try {
        if (itemId) {
          const payload = { ...itemData };
          // Stock quantity is display-only on edit - for EVERY item, including
          // one that only just had Track Stock switched on. Opening stock is
          // set once, when the item is created; after that stock only moves
          // through purchases / sales / stock adjustments. Keep the stored
          // opening / current qty so saving the form cannot overwrite it.
          if (item) {
            payload.units = {
              ...payload.units,
              stock: {
                ...payload.units?.stock,
                quantity: item.units?.stock?.quantity,
              },
            };
            payload.stockQty = {
              ...payload.stockQty,
              initialQty: item.stockQty?.initialQty,
              currentQty: item.stockQty?.currentQty,
            };
          }
          await dispatch(updateItem(itemId, payload, openSnackbar));
        } else {
          // Built as fresh objects (never mutating itemData's nested state),
          // and tolerant of any missing branch, so a partially-filled form
          // can't throw here and silently skip the create.
          const formUnits = itemData.units || {};
          const formStockQty = itemData.stockQty || {};
          const openingQty = parseFloat(formUnits.stock?.quantity) || 0;
          const updatedItemData = {
            ...itemData,
            vat: effectiveProfile?.commonVatRate || 0,
            category: itemData.category?.categoryId
              ? itemData.category
              : { categoryName: "", categoryId: null },
            units: {
              sale: { ...(formUnits.sale || {}) },
              purchase: { ...(formUnits.purchase || {}) },
              stock: itemData.toggleStock
                ? { ...(formUnits.stock || {}), quantity: openingQty }
                : { unit: null, quantity: 0 },
            },
            stockQty: {
              initialQty: itemData.toggleStock ? openingQty : 0,
              currentQty: itemData.toggleStock ? openingQty : 0,
              minQty: formStockQty.minQty || 0,
              maxQty: formStockQty.maxQty || 0,
            },
          };
          // barcode is a unique index; a blank "" collides with any other
          // item saved blank (409). Omitted, the server defaults it to the id.
          if (!String(updatedItemData.barcode ?? "").trim()) {
            delete updatedItemData.barcode;
          }
          const createdItem = await dispatch(createItem(updatedItemData, openSnackbar));
          onCreated?.(createdItem);
        }
        clear();
        handleClose();
      } catch (error) {
        // API failures are already reported by createItem/updateItem; this
        // catches anything else so a failed save is never silent.
        console.error("Failed to save item:", error);
        if (!error?.response) {
          openSnackbar(`Failed to save item: ${error?.message || "unknown error"}`);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const clearFieldError = (field) => {
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (!name || (typeof value === "string" && value.startsWith("__create_"))) return;

    clearFieldError(name);
    setItemData((prevItemData) => {
      const keys = name.split(".");
      const updatedData = { ...prevItemData };
      let currentLevel = updatedData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!currentLevel[key]) {
          currentLevel[key] = {};
        }
        currentLevel = currentLevel[key];
      }

      // Handle dropdowns explicitly
      if (name.includes("unit")) {
        currentLevel[keys[keys.length - 1]] = value; // Assign the _id directly
      } else {
        currentLevel[keys[keys.length - 1]] = value;
      }

      return updatedData;
    });
  };

  // const handleChange = (event) => {
  //   const { name, value } = event.target;

  //   if (!name) return; // Ensure the input has a name

  //   setItemData((prevItemData) => {
  //     // Split the name into keys for nested updates
  //     const keys = name.split(".");

  //     // Create a deep copy of the existing data
  //     const updatedData = { ...prevItemData };

  //     // Recursively traverse and set the value at the appropriate depth
  //     let currentLevel = updatedData;
  //     for (let i = 0; i < keys.length - 1; i++) {
  //       const key = keys[i];

  //       // Ensure the object exists at each level
  //       if (!currentLevel[key]) {
  //         currentLevel[key] = {};
  //       }
  //       currentLevel = currentLevel[key];
  //     }

  //     // Set the value at the last level
  //     currentLevel[keys[keys.length - 1]] = value;

  //     return updatedData;
  //   });
  // };

  // function handleFileChange(event) {
  //   const file = event.target.files[0];
  //   if (file) {
  //     const reader = new FileReader();

  //     reader.onload = (e) => {
  //       const base64ImageData = e.target.result;
  //       setItemData({ ...itemData, img: base64ImageData });
  //     };

  //     reader.readAsDataURL(file);
  //   }
  // }

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const url = "https://api.cloudinary.com/v1_1/almpo/image/upload"; // Replace 'almpo' with your Cloudinary cloud name
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "invoice"); // Replace with your upload preset

    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      setItemData({ ...itemData, img: data.secure_url }); // Store Cloudinary URL
    } catch (error) {
      console.error("Error uploading to Cloudinary:", error);
    }
  };

  // const handleOptionChange = (selectedOption) => {
  //   setOption(selectedOption);
  //   // if (selectedOption === "fileUpload" && !itemId) {
  //   //   history.push("/bulk-upload"); // Redirect to bulk upload page
  //   //   handleClose(); // Close the dialog
  //   // }
  // };

  // Error keys are the fields' own `name` paths, so handleChange can clear a
  // field's message the moment the user edits it.
  const validateForm = () => {
    const newErrors = {};
    const isBlank = (value) => value === "" || value == null || String(value).trim() === "";
    const toNumber = (value) => (isBlank(value) ? NaN : Number(value));
    // The stock section is hidden for the "Services" category, so its
    // fields must not produce errors the user can't see.
    const stockTracked =
      !!itemData.toggleStock && itemData.category?.categoryName !== "Services";

    // Name: required, sane length, unique in the catalog (ignoring case and
    // surrounding spaces; the item being edited doesn't clash with itself).
    const itemName = String(itemData.itemName || "").trim();
    if (!itemName) {
      newErrors.itemName = "Item name is required";
    } else if (itemName.length < 2) {
      newErrors.itemName = "Item name must be at least 2 characters";
    } else if (itemName.length > 100) {
      newErrors.itemName = "Item name must be 100 characters or fewer";
    } else if (
      allItems.some(
        (existing) =>
          !existing.isDeleted &&
          existing._id !== itemId &&
          String(existing.itemName || "").trim().toLowerCase() === itemName.toLowerCase()
      )
    ) {
      newErrors.itemName = `An item named "${itemName}" already exists`;
    }

    if (!itemData.category?.categoryId) {
      newErrors.category = "Select a category";
    }

    if (!itemData.units?.sale?.unit) {
      newErrors["units.sale.unit"] = "Select a sales unit";
    }

    const salePrice = toNumber(itemData.units?.sale?.price);
    if (isBlank(itemData.units?.sale?.price)) {
      newErrors["units.sale.price"] = "Sales price is required";
    } else if (!Number.isFinite(salePrice) || salePrice <= 0) {
      newErrors["units.sale.price"] = "Sales price must be greater than 0";
    }

    // Optional - blank means no minimum.
    const minSaleQtyRaw = itemData.units?.sale?.minQty;
    if (minSaleQuantityFeatureOn && !isBlank(minSaleQtyRaw)) {
      const minSaleQty = toNumber(minSaleQtyRaw);
      if (!Number.isFinite(minSaleQty) || minSaleQty <= 0) {
        newErrors["units.sale.minQty"] = "Minimum sale quantity must be a positive number";
      }
    }

    const costRaw = itemData.units?.purchase?.cost;
    if (!isBlank(costRaw)) {
      const cost = toNumber(costRaw);
      if (!Number.isFinite(cost) || cost < 0) {
        newErrors["units.purchase.cost"] = "Purchase cost cannot be negative";
      } else if (!itemData.units?.purchase?.unit) {
        newErrors["units.purchase.unit"] = "Select the unit this cost is for";
      }
    }

    // Barcode: optional, digits only, unique. An item saved without one gets
    // its own id (hex) as the barcode server-side, so the format rules only
    // apply to a barcode the user actually typed/changed here.
    const barcode = String(itemData.barcode ?? "").trim();
    const barcodeUnchanged = !!item && barcode === String(item.barcode ?? "").trim();
    if (barcode && !barcodeUnchanged) {
      if (!/^\d+$/.test(barcode)) {
        newErrors.barcode = "Barcode can contain digits only";
      } else if (barcode.length > 30) {
        newErrors.barcode = "Barcode must be 30 digits or fewer";
      } else if (
        allItems.some(
          (existing) =>
            !existing.isDeleted &&
            existing._id !== itemId &&
            String(existing.barcode ?? "").trim() === barcode
        )
      ) {
        newErrors.barcode = "This barcode is already used by another item";
      }
    }

    if (showItemVatField && !isBlank(itemData.vat)) {
      const vat = toNumber(itemData.vat);
      if (!Number.isFinite(vat) || vat < 0 || vat > 100) {
        newErrors.vat = `${taxLabel} must be between 0 and 100`;
      }
    }

    if (stockTracked) {
      if (!itemData.units?.stock?.unit) {
        newErrors["units.stock.unit"] = "Select a stock unit";
      }
      // Opening stock is only editable on create.
      if (!itemId) {
        const openingRaw = itemData.units?.stock?.quantity;
        const opening = isBlank(openingRaw) ? 0 : toNumber(openingRaw);
        if (!Number.isFinite(opening) || opening < 0) {
          newErrors["units.stock.quantity"] = "Opening stock cannot be negative";
        }
        if (
          company?.batchExpiryEnabled === true &&
          itemData.trackExpiry &&
          opening > 0 &&
          !itemData.openingExpiryDate
        ) {
          newErrors["openingExpiryDate"] = "Enter the expiry date of the opening stock";
        }
      }
      const minRaw = itemData.stockQty?.minQty;
      const maxRaw = itemData.stockQty?.maxQty;
      const minQty = isBlank(minRaw) ? 0 : toNumber(minRaw);
      const maxQty = isBlank(maxRaw) ? 0 : toNumber(maxRaw);
      if (!Number.isFinite(minQty) || minQty < 0) {
        newErrors["stockQty.minQty"] = "Min qty cannot be negative";
      }
      if (!Number.isFinite(maxQty) || maxQty < 0) {
        newErrors["stockQty.maxQty"] = "Max qty cannot be negative";
      }
      if (
        !newErrors["stockQty.minQty"] &&
        !newErrors["stockQty.maxQty"] &&
        maxQty > 0 &&
        minQty > maxQty
      ) {
        newErrors["stockQty.minQty"] = "Min qty cannot be more than max qty";
      }
    }

    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    if (!isValid) openSnackbar("Please fix the highlighted fields");
    return isValid;
  };

  const clear = () => {
    setItemData(createEmptyItemData(effectiveProfile?.commonVatRate || ""));
    setErrors({});
    setTrackStock(false);
  };

  useEffect(() => {
    let cancelled = false;
    if (item) {
      setItemData({
        ...item,
        units: {
          sale: {
            unit: item.units?.sale?.unit?._id || null, // Store only the ID
            price: item.units?.sale?.price || "",
            minQty: item.units?.sale?.minQty || "",
          },
          purchase: {
            unit: item.units?.purchase?.unit?._id || null, // Store only the ID
            cost: item.units?.purchase?.cost || "",
          },
          stock: {
            unit: item.units?.stock?.unit?._id || null, // Store only the ID
            quantity: item.units?.stock?.quantity || 0,
          },
        },
        category: {
          categoryName: item.category?.categoryName || "",
          categoryId: item.category?.categoryId || null,
        },
      });
      setIsEditMode(true);
      if (item.toggleStock && item._id && getCurrentStockQty) {
        // The lazy fetch can resolve after the dialog closed / the form was
        // cleared / the component unmounted - drop the result in that case.
        Promise.resolve(getCurrentStockQty(item._id))
          .then(({ currentStock } = {}) => {
            if (cancelled) return;
            const liveQty = parseFloat(currentStock);
            if (!Number.isFinite(liveQty)) return;
            setItemData((prev) => {
              if (!prev?.units) return prev;
              return {
                ...prev,
                units: {
                  ...prev.units,
                  stock: {
                    ...prev.units.stock,
                    quantity: liveQty,
                  },
                },
              };
            });
          })
          .catch((error) => {
            if (!cancelled) console.error("Failed to load live stock:", error);
          });
      }
    } else {
      // When opening for a new item, reset the form and switch to add mode
      clear();
      setIsEditMode(false);
      // Pre-fill from the search text of whatever "create new item" dropdown
      // triggered this (Purchase/Stock Adjustment quick-create) - only
      // meaningful while opening fresh, so gated on `open` too.
      if (open && initialItemName) {
        setItemData((current) => ({ ...current, itemName: initialItemName }));
      }
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, open]);

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem("profile")));
  }, [location]);

  // useEffect(() => {
  //   var checkId = user?.result?._id;
  //   if (checkId !== undefined) {
  //     setItemData({ ...itemData, itemId: [checkId] });
  //   } else {
  //     setItemData({ ...itemData, itemId: [user?.result?.googleId] });
  //   }
  // }, [location]);

  const handleClose = () => {
    setItemData(createEmptyItemData(effectiveProfile?.commonVatRate || ""));
    setErrors({});
    // ItemContext is shared app-wide; leaving the edited item's id in it makes
    // the next AddItem instance (e.g. Purchase/Stock Adjustment quick-create)
    // load that item's data instead of a blank form.
    setItemId?.(null);
    setOpen(false);
  };
  // const handleOptionChange = (selectedOption) => {
  //   setOption(selectedOption);
  // };
  // Same app theme, but every text field / select inside this dialog is the
  // compact ("small") size, so the form needs far less height.
  // Built from the current theme (the app's, or MUI's default when there is none)
  // and passed as a plain object - a theme *function* is not called by MUI when
  // there is no outer provider.
  const compactTheme = React.useMemo(
    () =>
      createTheme({
        ...theme,
        props: {
          ...(theme?.props || {}),
          MuiTextField: { ...(theme?.props?.MuiTextField || {}), size: "small" },
          MuiFormControl: { ...(theme?.props?.MuiFormControl || {}), size: "small" },
        },
        // Compulsory fields: the * after the label is red.
        overrides: {
          ...(theme?.overrides || {}),
          MuiFormLabel: {
            ...(theme?.overrides?.MuiFormLabel || {}),
            asterisk: { color: "#dc2626", fontWeight: 700 },
          },
        },
      }),
    [theme]
  );

  return (
    <ThemeProvider theme={compactTheme}>
    <div>
      <Dialog
        onClose={(event, reason) => {
          if (keepItemDialogOpenRef.current) return;
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
        fullScreen={isMobile}
        maxWidth="md"
        PaperProps={{
          style: {
            maxHeight: isMobile ? "100vh" : "94vh",
            borderRadius: isMobile ? 0 : 16,
          },
        }}
      >
        <DialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
          style={{
            paddingLeft: "20px",
            paddingTop: 8,
            paddingBottom: 8,
            color: "white",
            backgroundColor: "#ef7f1a",
          }}
        >
          {itemId ? "Edit Item" : "New Item"}
          {itemId && itemData?.itemName ? (
            <span style={{ fontWeight: 400, opacity: 0.9 }}> · {itemData.itemName}</span>
          ) : null}
        </DialogTitle>
        <DialogContent dividers style={{ background: "#f8fafc", padding: isMobile ? 10 : "12px 16px" }}>
          {/* {!itemId && (
            <FormGroup row >
              <FormControlLabel
                className={classes.radioLabel}
                control={
                  <Radio
                    checked={option === "manualAdd"}
                    onChange={() => handleOptionChange("manualAdd")}
                    value="manualAdd"
                    color="primary"
                  />
                }
                label="Manually Add Item"
              />

              <FormControlLabel
                control={
                  <Radio
                    checked={option === "fileUpload"}
                    onChange={() => handleOptionChange("fileUpload")}
                    value="fileUpload"
                    color="primary"
                  />
                }
                label="Bulk Upload via File"
              />
            </FormGroup>
          )} */}
          {/* {option === "fileUpload" && (
            <FileUpload setOpen={setOpen} open={open}  />
          )} */}
          {/* {option === "fileUpload" && (
            <Link to="/bulk-upload" style={{ marginLeft: 16 }}>
              <Button variant="outlined" color="primary"  onClick={() => (window.location.href = "/bulk-upload")}>
                Bulk Upload Items
              </Button>
            </Link>
          )} */}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-start" }}>
            <Container
              className={classes.container}
              style={{
                background: "#fff",
                padding: isMobile ? 10 : "6px 16px 14px",
                flex: "1 1 100%",
                maxWidth: "none",
                margin: 0,
                width: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              }}
            >
              <Grid container spacing={1}>
                <SectionTitle order={1}>Item details</SectionTitle>
                <Grid item xs={12} sm={6} style={{ order: 2 }}>
                  <TextField
                    autoFocus={!itemId}
                    label="Name"
                    variant="outlined"
                    className={classes.inputStyle}
                    name="itemName"
                    type="text"
                    required
                    onChange={handleChange}
                    value={itemData.itemName ?? ""}
                    error={!!errors.itemName}
                    helperText={errors.itemName}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                    <FormControl
                      variant="outlined"
                      className={classes.formControl}
                    >
                      <InputLabel id="demo-simple-select-outlined-label">
                        Category
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-outlined-label"
                        id="demo-simple-select-outlined"
                        value={
                          itemData.category ? itemData.category.categoryId : ""
                        }
                        onChange={(e) => {
                          const selectedCategoryId = e.target.value;
                          const selectedCategory = categories.find(
                            (category) => category._id === selectedCategoryId
                          );

                          setItemData({
                            ...itemData,
                            category: {
                              categoryName: selectedCategory
                                ? selectedCategory?.categoryName
                                : "",
                              categoryId: selectedCategoryId,
                            },
                          });
                        }}
                        label="Category"
                      >
                        {categories.map((category, index) => (
                          <MenuItem key={index} value={category._id}>
                            {category.categoryName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid> */}
                <Grid item xs={12} sm={6} style={{ order: 3 }}>
                  <FormControl
                    variant="outlined"
                    className={classes.formControl}
                    required
                    error={!!errors.category}
                  >
                    <InputLabel id="category-label">Category</InputLabel>
                    <Select
                      labelId="category-label"
                      id="category-select"
                      value={itemData?.category?.categoryId || ""}
                      onChange={(e) => {
                        const selectedCategoryId = e.target.value;
                        if (
                          typeof selectedCategoryId === "string" &&
                          selectedCategoryId.startsWith("__create_")
                        ) {
                          return;
                        }
                        const selectedCategory = categories.find(
                          (category) => category._id === selectedCategoryId
                        );
                        clearFieldError("category");
                        setItemData({
                          ...itemData,
                          category: {
                            categoryName: selectedCategory?.categoryName || "",
                            categoryId: selectedCategoryId,
                          },
                        });
                      }}
                      label="Category"
                      MenuProps={menuProps}
                      onOpen={() => setCategorySearch("")}
                    >
                      <SearchListHeader
                        classes={classes}
                        searchValue={categorySearch}
                        onChange={(event) => setCategorySearch(event.target.value)}
                        placeholder="Search categories..."
                      />
                      {filteredCategories.length === 0 && (
                        <MenuItem disabled className={classes.noResultsItem}>
                          No categories found
                        </MenuItem>
                      )}
                      {filteredCategories.map((category) => (
                        <MenuItem
                          key={category._id}
                          value={category._id}
                          disabled={!category.isActive} // Disable if inactive
                          className={
                            !category.isActive ? classes.disabledMenuItem : ""
                          }
                        >
                          {category.isActive
                            ? category.categoryName
                            : `${category.categoryName} (INACTIVE)`}
                        </MenuItem>
                      ))}
                      <MenuItem
                        className={classes.quickCreateItem}
                        value="__create_category__"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          openQuickCreate("category");
                        }}
                      >
                        <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                        Create new category
                      </MenuItem>
                    </Select>
                    {errors.category && (
                      <FormHelperText>{errors.category}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* <Grid item xs={12} sm={6}>
                    <TextField
                      label="Sales Rate"
                      variant="outlined"
                      className={classes.inputStyle}
                      name="unitPrice"
                      type="text"
                      required
                      onChange={handleChange}
                      value={itemData.unitPrice ?? ""}
                      error={!!errors.unitPrice} // Check if error exists
                      helperText={errors.unitPrice} // Display error message
                    />
                  </Grid> */}
                {/* <Grid item xs={12} sm={6}>
                    <TextField
                      label="Purchase Rate"
                      variant="outlined"
                      className={classes.inputStyle}
                      name="costPrice"
                      type="number"
                      onChange={handleChange}
                      value={itemData.costPrice ?? ""}
                      error={!!errors.costPrice} // Check if error exists
                      helperText={errors.costPrice} // Display error message
                    />
                  </Grid> */}

                <GroupLabel order={4} color="#16a34a">Selling</GroupLabel>
                <Grid item xs={12} sm={6} style={{ order: 5 }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    required
                    error={!!errors["units.sale.unit"]}
                  >
                    <InputLabel id="sales-unit-label">Sales Unit</InputLabel>
                    <Select
                      labelId="sales-unit-label" // Link the InputLabel to the Select
                      id="sales-unit-select" // Optional but recommended for accessibility
                      name="units.sale.unit"
                      value={itemData?.units?.sale?.unit || ""}
                      onChange={handleChange}
                      label="Sales Unit" // Ensure the label prop matches the InputLabel text
                      MenuProps={menuProps}
                      onOpen={() => setSaleUnitSearch("")}
                    >
                      <SearchListHeader
                        classes={classes}
                        searchValue={saleUnitSearch}
                        onChange={(event) => setSaleUnitSearch(event.target.value)}
                        placeholder="Search sales unit..."
                      />
                      {filteredSaleUnits.length === 0 && (
                        <MenuItem disabled className={classes.noResultsItem}>
                          No units found
                        </MenuItem>
                      )}
                      {filteredSaleUnits.map((unit) => (
                        <MenuItem key={unit._id} value={unit._id}>
                          {unit.name} ({unit.abbreviation})
                        </MenuItem>
                      ))}
                      <MenuItem
                        className={classes.quickCreateItem}
                        value="__create_sale_unit__"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          openQuickCreate("sale");
                        }}
                      >
                        <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                        Create new unit
                      </MenuItem>
                    </Select>
                    {errors["units.sale.unit"] && (
                      <FormHelperText>{errors["units.sale.unit"]}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} style={{ order: 6 }}>
                  <TextField
                    label="Sales Price"
                    name="units.sale.price"
                    variant="outlined"
                    className={classes.inputStyle}
                    type="number"
                    value={itemData?.units?.sale?.price ?? ""}
                    onChange={handleChange}
                    required
                    error={!!errors["units.sale.price"]}
                    helperText={errors["units.sale.price"]}
                    inputProps={{ step: "0.001", min: "0" }}
                  />
                  {!errors["units.sale.price"] && (
                    <FormHelperText style={{ marginTop: 4 }}>
                      Price per {selectedSaleUnit?.abbreviation || "sale unit"}
                    </FormHelperText>
                  )}
                </Grid>

                {minSaleQuantityFeatureOn && (
                  <Grid item xs={12} sm={6} style={{ order: 15 }}>
                    <TextField
                      label={`Minimum Sale Quantity${selectedSaleUnit?.abbreviation ? ` (${selectedSaleUnit.abbreviation})` : ""}`}
                      name="units.sale.minQty"
                      variant="outlined"
                      className={classes.inputStyle}
                      type="number"
                      value={itemData?.units?.sale?.minQty ?? ""}
                      onChange={handleChange}
                      error={!!errors["units.sale.minQty"]}
                      helperText={errors["units.sale.minQty"]}
                      inputProps={{ step: "0.001", min: "0" }}
                    />
                    {!errors["units.sale.minQty"] && (
                      <FormHelperText style={{ marginTop: 4 }}>
                        Optional - leave blank for no minimum.
                      </FormHelperText>
                    )}
                  </Grid>
                )}

                <GroupLabel order={7} color="#2563eb">Purchasing</GroupLabel>
                <Grid item xs={12} sm={6} style={{ order: 8 }}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!errors["units.purchase.unit"]}
                  >
                    <InputLabel id="purchase-unit-label">
                      Purchase Unit
                    </InputLabel>
                    <Select
                      labelId="purchase-unit-label" // Link the InputLabel to the Select
                      id="purchase-unit-select" // Optional but recommended for accessibility
                      name="units.purchase.unit"
                      MenuProps={menuProps}
                      value={itemData?.units?.purchase?.unit || ""}
                      onChange={handleChange}
                      label="Purchase Unit" // Ensure the label prop matches the InputLabel text
                      onOpen={() => setPurchaseUnitSearch("")}
                    >
                      <SearchListHeader
                        classes={classes}
                        searchValue={purchaseUnitSearch}
                        onChange={(event) => setPurchaseUnitSearch(event.target.value)}
                        placeholder="Search purchase unit..."
                      />
                      {filteredPurchaseUnits.length === 0 && (
                        <MenuItem disabled className={classes.noResultsItem}>
                          No units found
                        </MenuItem>
                      )}
                      {filteredPurchaseUnits.map((unit) => (
                        <MenuItem key={unit._id} value={unit._id}>
                          {unit.name} ({unit.abbreviation})
                        </MenuItem>
                      ))}
                      <MenuItem
                        className={classes.quickCreateItem}
                        value="__create_purchase_unit__"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          openQuickCreate("purchase");
                        }}
                      >
                        <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                        Create new unit
                      </MenuItem>
                    </Select>
                    {errors["units.purchase.unit"] && (
                      <FormHelperText>{errors["units.purchase.unit"]}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} style={{ order: 9 }}>
                  <TextField
                    label="Opening Purchase Cost"
                    name="units.purchase.cost"
                    type="number"
                    variant="outlined"
                    className={classes.inputStyle}
                    value={itemData?.units?.purchase?.cost ?? ""}
                    onChange={handleChange}
                    error={!!errors["units.purchase.cost"]}
                    helperText={errors["units.purchase.cost"]}
                    inputProps={{ step: "0.001", min: "0" }}
                  />
                </Grid>
                <GroupLabel order={11} color="#ea580c">Barcode &amp; image</GroupLabel>
                <Grid item xs={12} sm={6} style={{ order: 12 }}>
                  <TextField
                    label="Barcode"
                    variant="outlined"
                    className={classes.inputStyle}
                    name="barcode"
                    type="number"
                    onChange={handleChange}
                    value={itemData.barcode ?? ""}
                    error={!!errors.barcode} // Check if error exists
                    helperText={errors.barcode} // Display error message
                  />
                </Grid>

                {showItemVatField && (
                    <Grid item xs={12} sm={6} style={{ order: 14 }}>
                      <TextField
                        label={taxLabel}
                        variant="outlined"
                        className={classes.inputStyle}
                        name="vat"
                        type="number"
                        onChange={handleChange}
                        value={itemData.vat ?? ""}
                        error={!!errors.vat} // Check if error exists
                        helperText={errors.vat} // Display error message
                      />
                    </Grid>
                  )}

                {/* Margin check: only when sale and purchase unit are the same, otherwise a
                    price per sale unit and a cost per purchase unit are not comparable. */}
                {(() => {
                  const price = Number(itemData?.units?.sale?.price);
                  const cost = Number(itemData?.units?.purchase?.cost);
                  const saleUnit = itemData?.units?.sale?.unit;
                  const purchaseUnit = itemData?.units?.purchase?.unit;
                  if (!(price > 0) || !(cost > 0) || (saleUnit && purchaseUnit && saleUnit !== purchaseUnit)) return null;
                  const margin = ((price - cost) / price) * 100;
                  const loss = margin < 0;
                  return (
                    <Grid item xs={12} style={{ order: 10, paddingTop: 0 }}>
                      <Typography
                        style={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: loss ? "#b91c1c" : "#166534",
                          background: loss ? "#fef2f2" : "#f0fdf4",
                          borderRadius: 8,
                          padding: "4px 10px",
                          display: "inline-block",
                        }}
                      >
                        {loss
                          ? "Cost is higher than the sale price - this item sells at a loss"
                          : `Margin ${Number(margin.toFixed(1))}% (${Number((price - cost).toFixed(3))} per unit)`}
                      </Typography>
                    </Grid>
                  );
                })()}

                <Grid item xs={12} sm={6} style={{ order: 13 }}>
                  {!itemData.img ? (
                    <>
                      <input
                        accept="image/*"
                        className={classes.input}
                        onChange={handleFileChange}
                        id="icon-button-file"
                        type="file"
                      />
                      <label htmlFor="icon-button-file" style={{ display: "block" }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          component="span"
                          startIcon={<PhotoCamera />}
                          fullWidth
                          style={{ height: 40, textTransform: "none", borderStyle: "dashed", borderWidth: 1.5, background: "#fff7ed", fontWeight: 600 }}
                        >
                          Upload Image
                        </Button>
                      </label>
                    </>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        height: 40,
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        padding: "0 8px",
                        background: "#f8fafc",
                      }}
                    >
                      <img
                        src={itemData.img}
                        alt="Item"
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          objectFit: "cover",
                        }}
                      />
                      <Typography variant="body2" style={{ flex: 1 }}>
                        Image selected
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setItemData((prevData) => ({
                            ...prevData,
                            img: null,
                          }))
                        }
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                  )}
                </Grid>

                {/* <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      label="Is Staff Required?"
                      control={
                        <Switch
                          checked={itemData?.isStaffReq}
                          onChange={handleIsStaffRequiredChange}
                          name="isStaffReq"
                          color="primary"
                        />
                      }
                    />
                  </Grid> */}
              </Grid>
            </Container>
            {itemData.category.categoryName != "Services" && (
              <Container
                className={classes.container}
                style={{
                  background: "#fff",
                  padding: isMobile ? 10 : "10px 16px 14px",
                  flex: "1 1 100%",
                  maxWidth: "none",
                  margin: 0,
                  width: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: 14,
                  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                }}
              >
                <Typography
                  style={{
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                    color: "#9a3412",
                    textTransform: "uppercase",
                    borderBottom: "1px solid #fed7aa",
                    paddingBottom: 4,
                    marginBottom: 6,
                  }}
                >
                  Stock
                </Typography>
                <FormControlLabel
                  label="Track Stock"
                  control={
                    <Switch
                      checked={
                        itemData?.toggleStock
                          ? itemData.toggleStock
                          : trackStock
                      }
                      onChange={handleTrackStockChange}
                      name="trackStock"
                      color="primary"
                    />
                  }
                />
                {itemData.toggleStock && company?.batchExpiryEnabled === true && (
                  <FormControlLabel
                    label="Track Expiry (batch-wise)"
                    control={
                      <Switch
                        checked={!!itemData.trackExpiry}
                        onChange={(event) =>
                          setItemData({ ...itemData, trackExpiry: event.target.checked })
                        }
                        name="trackExpiry"
                        color="primary"
                      />
                    }
                  />
                )}
                {itemId && itemData.toggleStock && itemData.trackExpiry && company?.batchExpiryEnabled === true && (
                  <Typography style={{ fontSize: 12, color: "#64748b", margin: "2px 0 8px" }}>
                    Batch and expiry date are entered on each Purchase or Stock Adjustment (Add) - the opening batch fields only show when creating an item.
                  </Typography>
                )}
                {itemData.toggleStock && (
                  <>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4} style={{ order: 1 }}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          required
                          error={!!errors["units.stock.unit"]}
                        >
                          <InputLabel id="stock-unit-label">
                            Stock Unit
                          </InputLabel>
                          <Select
                            labelId="stock-unit-label" // Link the InputLabel to the Select
                            id="stock-unit-select" // Optional but recommended for accessibility
                            name="units.stock.unit"
                            value={itemData?.units?.stock?.unit || ""}
                            onChange={handleChange}
                            MenuProps={menuProps}
                            label="Stock Unit" // Ensure the label prop matches the InputLabel text
                            onOpen={() => setStockUnitSearch("")}
                          >
                            <SearchListHeader
                              classes={classes}
                              searchValue={stockUnitSearch}
                              onChange={(event) => setStockUnitSearch(event.target.value)}
                              placeholder="Search stock unit..."
                            />
                            {filteredStockUnits.length === 0 && (
                              <MenuItem disabled className={classes.noResultsItem}>
                                No units found
                              </MenuItem>
                            )}
                            {filteredStockUnits.map((unit) => (
                              <MenuItem key={unit._id} value={unit._id}>
                                {unit.name} ({unit.abbreviation})
                              </MenuItem>
                            ))}
                            <MenuItem
                              className={classes.quickCreateItem}
                              value="__create_stock_unit__"
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                openQuickCreate("stock");
                              }}
                            >
                              <AddIcon fontSize="small" style={{ marginRight: 8 }} />
                              Create new unit
                            </MenuItem>
                          </Select>
                          {errors["units.stock.unit"] && (
                            <FormHelperText>{errors["units.stock.unit"]}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={4} style={{ order: 2 }}>
                        <TextField
                          label={itemId ? "Current Stock" : "Opening Stock"}
                          name="units.stock.quantity"
                          type="number"
                          variant="outlined"
                          className={classes.inputStyle}
                          value={itemData?.units?.stock?.quantity ?? ""}
                          onChange={handleChange}
                          disabled={Boolean(itemId)}
                          inputProps={{ step: "0.001", min: "0" }}
                          error={!!errors["units.stock.quantity"]}
                          helperText={errors["units.stock.quantity"]}
                        />
                        <FormHelperText style={{ marginTop: 4 }}>
                          {itemId
                            ? "Can't be edited here — change stock with a purchase, sale, or stock adjustment"
                            : `Becomes this item's opening stock (in ${selectedStockUnit?.abbreviation || "stock unit"})`}
                        </FormHelperText>
                      </Grid>
                      {!itemId && company?.batchExpiryEnabled === true && itemData.trackExpiry && (
                        <>
                          <Grid item xs={12} sm={4} style={{ order: 5 }}>
                            <TextField
                              label="Opening Batch No"
                              variant="outlined"
                              className={classes.inputStyle}
                              value={itemData.openingBatchNo || ""}
                              onChange={(e) => setItemData({ ...itemData, openingBatchNo: e.target.value })}
                              placeholder="OPENING"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4} style={{ order: 6 }}>
                            <ExpiryDatePicker
                              label="Opening Expiry Date"
                              width="100%"
                              value={itemData.openingExpiryDate ? String(itemData.openingExpiryDate).slice(0, 10) : ""}
                              onChange={(v) => setItemData({ ...itemData, openingExpiryDate: v })}
                              error={!!errors["openingExpiryDate"]}
                              helperText={errors["openingExpiryDate"]}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} sm={4} style={{ order: 4 }}>
                        <TextField
                          label="Max Qty"
                          variant="outlined"
                          className={classes.inputStyle}
                          name="stockQty.maxQty"
                          type="number"
                          onChange={handleChange}
                          value={itemData?.stockQty?.maxQty ?? ""}
                          error={!!errors["stockQty.maxQty"]}
                          helperText={errors["stockQty.maxQty"]}
                          inputProps={{ min: "0" }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4} style={{ order: 3 }}>
                        <TextField
                          label="Min Qty"
                          variant="outlined"
                          className={classes.inputStyle}
                          name="stockQty.minQty"
                          type="number"
                          onChange={handleChange}
                          value={itemData?.stockQty?.minQty ?? ""}
                          error={!!errors["stockQty.minQty"]}
                          helperText={errors["stockQty.minQty"]}
                          inputProps={{ min: "0" }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </Container>
            )}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: "10px 20px", borderTop: "1px solid #e2e8f0", background: "#fff", justifyContent: "space-between" }}>
          <Typography style={{ fontSize: 12, color: "#94a3b8" }}>
            <span style={{ color: "#dc2626", fontWeight: 700 }}>*</span> Required fields
          </Typography>
          <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={handleClose} disabled={isSubmitting} style={{ textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitItem}
            variant="contained"
            disabled={!units.length || !categories.length || isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            style={{
              backgroundColor: "#ef7f1a",
              color: "white",
              textTransform: "none",
              fontWeight: 700,
              minWidth: 120,
            }}
          >
            {isSubmitting ? "Saving..." : itemId ? "Save Changes" : "Save Item"}
          </Button>
          </div>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(quickCreateType)}
        onClose={closeQuickCreate}
        aria-labelledby="quick-create-title"
        maxWidth="xs"
        fullWidth
      >
        <MuiDialogTitle id="quick-create-title">
          {quickCreateType === "category" ? "Create Category" : "Create Unit"}
        </MuiDialogTitle>
        <MuiDialogContent className={classes.quickDialogContent}>
          {quickCreateType === "category" ? (
            <TextField
              autoFocus
              label="Category name"
              value={quickCategoryName}
              onChange={(event) => setQuickCategoryName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleQuickCreate();
              }}
              variant="outlined"
              fullWidth
            />
          ) : (
            <>
              <TextField
                autoFocus
                label="Unit name"
                value={quickUnitName}
                onChange={(event) => setQuickUnitName(event.target.value)}
                variant="outlined"
                fullWidth
              />
              <TextField
                label="Abbreviation"
                value={quickUnitAbbreviation}
                onChange={(event) => setQuickUnitAbbreviation(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleQuickCreate();
                }}
                variant="outlined"
                fullWidth
                helperText="Example: Kilogram → kg"
              />
            </>
          )}
        </MuiDialogContent>
        <MuiDialogActions>
          <Button onClick={closeQuickCreate} disabled={isQuickCreating}>
            Cancel
          </Button>
          <Button
            onClick={handleQuickCreate}
            color="primary"
            variant="contained"
            disabled={isQuickCreating}
            startIcon={isQuickCreating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          >
            Create & Select
          </Button>
        </MuiDialogActions>
      </Dialog>
    </div>
    </ThemeProvider>
  );
};

export default AddItem;
